import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import {
  FiMessageCircle, FiPhone, FiVideo, FiUsers, FiBarChart2,
  FiShield, FiTarget, FiGlobe, FiCheck, FiArrowRight,
  FiLayers, FiTrendingUp, FiSmartphone, FiHexagon, FiPlay,
  FiZap, FiRefreshCw, FiCamera, FiMic, FiMail, FiCalendar
} from 'react-icons/fi';
import { BsWhatsapp } from 'react-icons/bs';
import { MdOutlinePrecisionManufacturing, MdOutlineRealEstateAgent } from 'react-icons/md';
import { FaCar, FaHospital, FaGraduationCap, FaShoppingBag } from 'react-icons/fa';

import logo from '../../Images/logo.png';

gsap.registerPlugin(ScrollTrigger);

const Home = () => {
  const navigate = useNavigate();
  const sectionRefs = useRef([]);
  const textRevealRefs = useRef([]);
  const horizontalContainerRef = useRef(null);
  const horizontalRef = useRef(null);
  const tiltRefs = useRef([]);

  const addToSectionRefs = (el) => {
    if (el && !sectionRefs.current.includes(el)) sectionRefs.current.push(el);
  };

  const addToTextRefs = (el) => {
    if (el && !textRevealRefs.current.includes(el)) textRevealRefs.current.push(el);
  };

  useEffect(() => {
    // Standard Fade Ups
    sectionRefs.current.forEach((el) => {
      gsap.fromTo(el, { opacity: 0, y: 60 }, {
        opacity: 1, y: 0, duration: 1.2, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' }
      });
    });

    // Staggered Text Reveals
    textRevealRefs.current.forEach((el) => {
      gsap.fromTo(el, { opacity: 0, y: 100 }, {
        opacity: 1, y: 0, duration: 1.5, ease: 'expo.out',
        scrollTrigger: { trigger: el, start: 'top 90%' }
      });
    });

    // Horizontal Scroll Trigger
    if (horizontalRef.current && horizontalContainerRef.current) {
      let totalWidth = 0;
      const panels = gsap.utils.toArray(horizontalRef.current.children);
      panels.forEach(panel => totalWidth += panel.offsetWidth);

      const scrollAmount = totalWidth - window.innerWidth;

      let mm = gsap.matchMedia();
      mm.add("(min-width: 1024px)", () => {
        gsap.to(panels, {
          xPercent: -100 * (panels.length - 1),
          ease: "none",
          scrollTrigger: {
            trigger: horizontalContainerRef.current,
            pin: true,
            scrub: 1,
            end: () => `+=${scrollAmount}`,
            invalidateOnRefresh: true
          }
        });
      });
    }

    return () => { ScrollTrigger.getAll().forEach((t) => t.kill()); };
  }, []);

  const handleMouseMove = (e, idx) => {
    const el = tiltRefs.current[idx];
    if (!el) return;
    const { left, top, width, height } = el.getBoundingClientRect();
    const x = (e.clientX - left) / width - 0.5;
    const y = (e.clientY - top) / height - 0.5;

    gsap.to(el, {
      duration: 0.6,
      rotateY: x * 15,
      rotateX: -y * 15,
      ease: 'power2.out',
      transformPerspective: 1000,
    });
  };

  const handleMouseLeave = (idx) => {
    const el = tiltRefs.current[idx];
    if (!el) return;
    gsap.to(el, { duration: 0.8, rotateY: 0, rotateX: 0, ease: 'power2.out' });
  };

  return (
    <div className="bg-slate-50 text-slate-900 min-h-screen font-sans overflow-x-hidden w-full m-0 p-0 selection:bg-indigo-600 selection:text-white">

      {/* Absolute Navbar */}
      <header className="absolute top-0  z-50">
        <div className="w-full mx-auto px-6 md:px-12 flex justify-between items-center gap-12">
          <img src={logo} alt="SaleSense Logo" className="h-10 object-contain mix-blend-multiply drop-shadow-sm ml-4" />
          <nav className="hidden md:flex gap-10 items-center text-sm font-medium tracking-wide">
            <a href="#modules" className="text-slate-600 hover:text-indigo-600 transition-colors">Platform</a>
            <a href="#ecosystem" className="text-slate-600 hover:text-indigo-600 transition-colors">Ecosystem</a>
            <a href="#capabilities" className="text-slate-600 hover:text-indigo-600 transition-colors">AI Capabilities</a>
            <a href="#industries" className="text-slate-600 hover:text-indigo-600 transition-colors">Industries</a>

          </nav>
          <div className="flex gap-4">
            <button className="px-5 py-2.5 text-slate-700 hover:text-indigo-600 font-semibold transition-all" onClick={() => navigate('/login')}>Sign In</button>
            <button className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full font-medium transition-all shadow-lg shadow-indigo-600/30 active:scale-95" onClick={() => navigate('/signup')}>Start Free Trial</button>
          </div>
        </div>
      </header>

      {/* 🟦 HERO SECTION */}
      <section className="relative pt-48 pb-32 px-6 md:px-12 max-w-7xl mx-auto min-h-screen flex flex-col justify-center z-10">

        {/* Floating Abstract UI Image behind text */}
        <div className="absolute top-40 right-0 w-[500px] h-[500px] rounded-[3rem] overflow-hidden opacity-20 pointer-events-none transform rotate-3 mix-blend-multiply hidden lg:block">
          <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070&auto=format&fit=crop" alt="Dashboard" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/50 to-transparent mix-blend-overlay"></div>
        </div>

        <div className="max-w-5xl relative z-10">
          <div className="overflow-hidden mb-6">
            <p ref={addToTextRefs} className="text-sm font-bold text-indigo-600 tracking-widest uppercase flex items-center gap-2">
              <span className="w-8 h-[2px] bg-indigo-600 inline-block"></span> A Product of Provaantech
            </p>
          </div>

          <div className="overflow-hidden mb-8">
            <h1 ref={addToTextRefs} className="text-6xl md:text-8xl lg:text-[7rem] font-extrabold leading-[0.95] text-slate-900 tracking-tighter">
              Turn Conversations Into <span className="text-transparent bg-clip-text bg-gradient-to-br from-indigo-600 to-blue-500">Conversions.</span>
            </h1>
          </div>

          <div className="overflow-hidden mb-12 max-w-3xl">
            <p ref={addToTextRefs} className="text-xl md:text-2xl text-slate-500 font-light leading-relaxed">
              Sailsense is an AI-powered sales & automation platform that helps businesses generate leads, engage customers, and close sales using <strong className="font-semibold text-slate-800">intelligent chatbots, WhatsApp automation, and AI-driven workflows.</strong>
            </p>
            <p ref={addToTextRefs} className="text-lg text-slate-400 mt-4">Built to replace manual follow-ups, slow response systems, and missed opportunities.</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-16" ref={addToSectionRefs}>
            <button className="px-8 py-4 bg-slate-900 hover:bg-slate-800 text-white rounded-full font-medium transition-all shadow-2xl shadow-slate-900/20 active:scale-95 flex items-center justify-center gap-3 text-lg group">
              <FiZap /> Start Free Trial
            </button>
            <button className="px-8 py-4 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-full font-medium transition-all active:scale-95 flex items-center justify-center gap-3 text-lg shadow-sm border border-indigo-100">
              <FiMessageCircle /> See AI Chatbot Demo
            </button>
            <button className="px-8 py-4 bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 rounded-full font-medium transition-all active:scale-95 flex items-center justify-center gap-3 text-lg shadow-sm">
              <FiPhone /> Book a Demo Call
            </button>
          </div>
        </div>
      </section>

      {/* 🟪 TRUST BAR */}
      <section className="py-16 border-y border-slate-200 bg-white z-10 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-8">
          <p className="text-sm font-semibold text-slate-400 uppercase tracking-widest whitespace-nowrap">
            Trusted by fast-growing businesses
          </p>
          <div className="flex flex-wrap justify-center md:justify-end gap-8 md:gap-12 text-slate-400 font-bold text-lg md:text-xl tracking-tight grayscale opacity-70">
            {['Real Estate', 'Automotive', 'Healthcare', 'Education', 'Retail', 'Services'].map((industry, i) => (
              <span key={i} className="hover:text-slate-800 hover:grayscale-0 transition-all duration-300 cursor-default">{industry}</span>
            ))}
          </div>
        </div>
      </section>

      {/* 🟩 CORE VALUE SECTION */}
      <section className="py-32 px-6 md:px-12 max-w-7xl mx-auto z-10 relative border-b border-slate-200">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div ref={addToSectionRefs}>
            <h2 className="text-5xl md:text-6xl font-bold text-slate-900 tracking-tighter mb-6">Stop Losing Leads.<br />Start Converting Instantly.</h2>
            <p className="text-xl text-slate-500 mb-6 font-light">
              Most businesses lose <span className="text-red-500 font-bold">60–80%</span> of leads due to slow response time. Sailsense solves this with AI automation that responds, qualifies, and nurtures leads instantly.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {[
                "Instant AI Responses",
                "Automated Lead Qualification",
                "Smart Follow-ups",
                "24/7 Customer Engagement"
              ].map((val, i) => (
                <div key={i} className="flex items-center gap-3 font-medium text-slate-700 bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                  <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0"><FiCheck /></div>
                  {val}
                </div>
              ))}
            </div>
          </div>
          <div className="relative" ref={addToSectionRefs}>
            <div className="w-full aspect-square rounded-[3rem] overflow-hidden shadow-2xl relative">
              <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2000&auto=format&fit=crop" alt="Dashboard stats" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-indigo-900/10 mix-blend-multiply"></div>
            </div>
          </div>
        </div>
      </section>

      {/* ⚡ CORE PRODUCT MODULES */}
      <section id="modules" className="py-32 bg-slate-50 z-10 relative border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-24" ref={addToSectionRefs}>
            <h2 className="text-5xl md:text-6xl font-bold tracking-tighter mb-6 text-black">Core Product Modules</h2>
            <p className="text-xl text-slate-500">The three pillars of your automated sales force.</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Module 1 */}
            <div ref={addToSectionRefs} className="bg-white p-10 rounded-[2rem] border border-slate-200 shadow-xl hover:shadow-2xl transition-all duration-300 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 text-8xl font-black text-slate-50 group-hover:text-indigo-50 transition-colors -z-0 select-none">1</div>
              <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-2xl flex items-center justify-center text-3xl mb-8 relative z-10"><FiMessageCircle /></div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2 relative z-10">AI Chatbot</h3>
              <p className="text-indigo-600 font-medium mb-6 relative z-10">Website + WhatsApp</p>
              <p className="text-slate-600 mb-8 relative z-10">Your Always-Available Sales Assistant.</p>
              <ul className="space-y-3 relative z-10">
                {["Human-like AI conversations", "Lead capture inside chat", "Product/service explanations", "WhatsApp + Website integration", "Multi-language support"].map((li, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-700 font-medium"><FiCheck className="text-indigo-600" /> {li}</li>
                ))}
              </ul>
            </div>

            {/* Module 2 */}
            <div ref={addToSectionRefs} className="bg-white p-10 rounded-[2rem] border border-slate-200 shadow-xl hover:shadow-2xl transition-all duration-300 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 text-8xl font-black text-slate-50 group-hover:text-rose-50 transition-colors -z-0 select-none">2</div>
              <div className="w-16 h-16 bg-rose-100 text-rose-600 rounded-2xl flex items-center justify-center text-3xl mb-8 relative z-10"><FiTarget /></div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2 relative z-10">Lead Generation Engine</h3>
              <p className="text-rose-600 font-medium mb-6 relative z-10">Capture & Convert Every Visitor</p>
              <ul className="space-y-3 relative z-10">
                {["Smart lead forms", "Behavior-based targeting", "Auto lead scoring", "CRM integration", "Campaign tracking"].map((li, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-700 font-medium"><FiCheck className="text-rose-600" /> {li}</li>
                ))}
              </ul>
            </div>

            {/* Module 3 */}
            <div ref={addToSectionRefs} className="bg-white p-10 rounded-[2rem] border border-slate-200 shadow-xl hover:shadow-2xl transition-all duration-300 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 text-8xl font-black text-slate-50 group-hover:text-blue-50 transition-colors -z-0 select-none">3</div>
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center text-3xl mb-8 relative z-10"><FiRefreshCw /></div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2 relative z-10">AI Automation System</h3>
              <p className="text-blue-600 font-medium mb-6 relative z-10">Never Miss a Follow-Up Again</p>
              <ul className="space-y-3 relative z-10">
                {["WhatsApp automation", "Email + SMS workflows", "CRM updates", "Sales pipeline automation", "Task & reminder triggers"].map((li, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-700 font-medium"><FiCheck className="text-blue-600" /> {li}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 🌐 SAILSENSE AI ECOSYSTEM */}
      <section id="ecosystem" className="py-32 px-6 md:px-12 max-w-7xl mx-auto z-10 relative">
        <div className="text-center mb-24" ref={addToSectionRefs}>
          <h2 className="text-5xl md:text-7xl font-bold text-slate-900 tracking-tighter mb-6">Sailsense AI Ecosystem</h2>
          <p className="text-2xl text-slate-500 font-light">One Platform. Complete Sales Growth System.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { step: "01", title: "ATTRACT", items: ["Ads & website traffic capture", "Social media lead intake"] },
            { step: "02", title: "ENGAGE", items: ["AI Chatbot conversations", "WhatsApp automation", "Email nurturing"] },
            { step: "03", title: "CONVERT", items: ["Lead scoring system", "Smart follow-ups", "Sales alerts"] },
            { step: "04", title: "ANALYZE", items: ["Performance dashboard", "Conversion tracking", "Customer insights"] }
          ].map((pillar, i) => (
            <div key={i} ref={addToSectionRefs} className="bg-slate-900 text-white p-8 rounded-3xl shadow-xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 text-6xl font-black text-white/5 transition-colors -z-0 select-none">{pillar.step}</div>
              <h3 className="text-2xl font-bold mb-8 relative z-10 text-indigo-400">{pillar.title}</h3>
              <ul className="space-y-4 relative z-10">
                {pillar.items.map((item, j) => (
                  <li key={j} className="text-slate-300 font-medium flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2 flex-shrink-0"></div> {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* 🧠 ADVANCED AI CAPABILITIES (Horizontal GSAP Showcase) */}
      <section id="capabilities" ref={horizontalContainerRef} className="h-screen flex items-center overflow-hidden bg-slate-900 text-white hidden lg:flex border-t border-white/10">
        <div ref={horizontalRef} className="flex h-full items-center pl-12 pr-[50vw]">

          <div className="w-[80vw] md:w-[40vw] flex-shrink-0 px-8">
            <h2 className="text-6xl md:text-8xl font-bold tracking-tighter leading-none">Advanced AI <br />Capabilities.</h2>
            <p className="text-2xl text-slate-400 mt-6 font-light">Next Generation AI Tools.</p>
            <FiArrowRight className="text-5xl text-indigo-500 mt-8" />
          </div>

          {[
            { icon: FiVideo, title: "AI Video-to-Video", subtitle: "Transform visuals instantly.", desc: "Automate and transform video content using cutting edge AI generation tools.", img: "https://images.unsplash.com/photo-1616469829941-c7200edec809?q=80&w=2000&auto=format&fit=crop" },
            { icon: FiMic, title: "Audio-to-Video", subtitle: "Turn voice into visuals.", desc: "Convert audio streams into dynamic, lip-synced video presentations automatically.", img: "https://images.unsplash.com/photo-1581351114358-940f281e05d4?q=80&w=2000&auto=format&fit=crop" },
            { icon: FiUsers, title: "AI Video Avatar", subtitle: "Your 24/7 Spokesperson.", desc: "Interactive AI humans that greet visitors, explain products, and capture leads on camera.", img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=2000&auto=format&fit=crop" },
            { icon: FiPhone, title: "AI Voice Calling Agent", subtitle: "Never miss a lead.", desc: "Human-like voice agents that dial internet leads instantly and book appointments.", img: "https://images.unsplash.com/photo-1516387938699-a93567ec168e?q=80&w=2000&auto=format&fit=crop" },
            { icon: FiSmartphone, title: "Meta Ads AI Optimization", subtitle: "Maximize ROAS.", desc: "AI engine that continuously monitors and optimizes your Meta ad spend and targeting.", img: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?q=80&w=2000&auto=format&fit=crop" },
            { icon: FiGlobe, title: "SEO Automation Engine", subtitle: "Dominate search.", desc: "Generate intent-driven content, cluster keywords, and automate your SEO strategy.", img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2000&auto=format&fit=crop" }
          ].map((engine, i) => (
            <div key={i} className="w-[80vw] md:w-[60vw] lg:w-[45vw] h-[70vh] flex-shrink-0 px-6 flex items-center">
              <div className="w-full h-full rounded-[3rem] shadow-2xl relative overflow-hidden group border border-white/5">
                <img src={engine.img} alt={engine.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-slate-900/80 group-hover:bg-slate-900/70 transition-colors backdrop-blur-[2px]"></div>

                <div className="relative z-10 p-12 h-full flex flex-col">
                  <div className="w-16 h-16 bg-white/10 backdrop-blur-md text-white rounded-2xl flex items-center justify-center text-3xl mb-8 border border-white/20 group-hover:scale-110 transition-transform duration-500">
                    <engine.icon />
                  </div>
                  <p className="text-indigo-400 font-bold uppercase tracking-wider text-sm mb-4">{engine.title}</p>
                  <h3 className="text-4xl font-bold text-white mb-6 tracking-tight">{engine.subtitle}</h3>
                  <p className="text-lg text-slate-300 mb-auto">{engine.desc}</p>

                  <div className="mt-8 pt-8 border-t border-white/10 flex items-center justify-between text-white font-medium group-hover:text-indigo-400 cursor-pointer transition-colors">
                    Explore Capability <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Mobile Fallback for Capabilities */}
      <section className="py-24 px-6 lg:hidden bg-slate-900 text-white space-y-12">
        <h2 className="text-5xl font-bold tracking-tighter mb-12">Advanced AI Capabilities.</h2>
        {[
          { icon: FiVideo, title: "AI Video-to-Video" },
          { icon: FiMic, title: "Audio-to-Video" },
          { icon: FiUsers, title: "AI Video Avatar" },
          { icon: FiPhone, title: "AI Voice Calling Agent" },
          { icon: FiSmartphone, title: "Meta Ads AI Optimization" },
          { icon: FiGlobe, title: "SEO Automation Engine" }
        ].map((engine, i) => (
          <div key={i} className="bg-white/5 border border-white/10 rounded-[2rem] p-8 backdrop-blur-md">
            <div className="w-16 h-16 bg-indigo-500/20 text-indigo-400 rounded-2xl flex items-center justify-center text-3xl mb-6"><engine.icon /></div>
            <h3 className="text-2xl font-bold text-white tracking-tight">{engine.title}</h3>
          </div>
        ))}
      </section>

      {/* 📊 KEY FEATURES */}
      <section className="py-32 px-6 md:px-12 max-w-7xl mx-auto z-10 relative">
        <div className="text-center mb-16" ref={addToSectionRefs}>
          <h2 className="text-5xl font-bold text-slate-900 tracking-tighter mb-4">Key Features</h2>
          <p className="text-xl text-slate-500">Built for Speed, Automation & Growth</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: FiZap, title: "Instant Lead Response System" },
            { icon: FiMessageCircle, title: "AI Chat Intelligence" },
            { icon: FiBarChart2, title: "Real-Time Analytics" },
            { icon: BsWhatsapp, title: "CRM + WhatsApp Sync" },
            { icon: FiTrendingUp, title: "Sales Tracking Automation" },
            { icon: FiLayers, title: "Smart Notifications" }
          ].map((item, i) => (
            <div
              key={i}
              className="bg-white p-8 rounded-3xl border border-slate-100 shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center"
              ref={(el) => { tiltRefs.current[i] = el; addToSectionRefs(el); }}
              onMouseMove={(e) => handleMouseMove(e, i)}
              onMouseLeave={() => handleMouseLeave(i)}
            >
              <div className="w-14 h-14 bg-slate-50 text-indigo-600 rounded-2xl flex items-center justify-center text-2xl mb-6">
                <item.icon />
              </div>
              <h3 className="text-lg font-bold text-slate-900">{item.title}</h3>
            </div>
          ))}
        </div>
      </section>

      {/* 🏢 INDUSTRY USE CASES */}
      <section id="industries" className="py-32 bg-slate-50 relative z-10 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-20" ref={addToSectionRefs}>
            <h2 className="text-5xl md:text-6xl font-bold text-slate-900 tracking-tighter mb-6">Industry Use Cases</h2>
            <p className="text-xl text-slate-500 font-light">Designed for Every Business Type</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: MdOutlineRealEstateAgent, name: "Real Estate", desc: "Property inquiry automation + follow-ups" },
              { icon: FaCar, name: "Automotive", desc: "Test drive booking + F&I automated invoicing + lead nurturing" },
              { icon: FaHospital, name: "Healthcare", desc: "Appointment scheduling + patient engagement" },
              { icon: FaGraduationCap, name: "Education", desc: "Admission inquiry automation + student support" },
              { icon: FaShoppingBag, name: "Retail", desc: "24/7 customer support chatbot + order updates" }
            ].map((ind, i) => (
              <div key={i} className="bg-white border border-slate-200 p-8 rounded-3xl shadow-sm hover:shadow-xl transition-all group" ref={addToSectionRefs}>
                <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center text-2xl mb-6 group-hover:scale-110 transition-transform"><ind.icon /></div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">{ind.name}</h3>
                <p className="text-slate-600">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 📈 RESULTS SECTION */}
      <section className="py-32 bg-slate-900 text-white relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-20" ref={addToSectionRefs}>
            <h2 className="text-5xl md:text-7xl font-bold tracking-tighter mb-6">Businesses using Sailsense achieve:</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-y-16 gap-x-8 text-center sm:text-left">
            {[
              { stat: "3X", text: "More Qualified Leads" },
              { stat: "Instant", text: "Faster Response Time" },
              { stat: "Higher", text: "Conversion Rates" },
              { stat: "-70%", text: "Reduced Manual Workload" }
            ].map((impact, i) => (
              <div key={i} ref={addToSectionRefs} className="border-t-4 border-indigo-500 pt-6">
                <h3 className="text-5xl font-black tracking-tighter mb-2 text-white">{impact.stat}</h3>
                <p className="text-lg text-indigo-200 font-medium">{impact.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 🚀 FINAL CTA SECTION */}
      <section className="py-40 bg-white relative overflow-hidden z-10 text-center">
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <h2 ref={addToTextRefs} className="text-5xl md:text-7xl font-extrabold tracking-tighter text-slate-900 mb-8">
            Start Growing With <br />Sailsense Today
          </h2>
          <p className="text-2xl text-slate-500 font-light mb-16" ref={addToSectionRefs}>
            No coding. No setup stress. <strong className="font-bold text-slate-800">Just plug into AI growth.</strong>
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6" ref={addToSectionRefs}>
            <button className="w-full sm:w-auto px-10 py-5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full font-bold transition-all shadow-xl active:scale-95 text-lg flex items-center justify-center gap-2">
              <FiZap /> Start Free Trial
            </button>
            <button className="w-full sm:w-auto px-10 py-5 bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 rounded-full font-bold transition-all active:scale-95 text-lg flex items-center justify-center gap-2">
              <FiMessageCircle /> Request Demo
            </button>
            <button className="w-full sm:w-auto px-10 py-5 bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 rounded-full font-bold transition-all active:scale-95 text-lg flex items-center justify-center gap-2">
              <FiBarChart2 /> View Live System
            </button>
          </div>
        </div>
      </section>

      {/* 🟦 FOOTER */}
      <footer className="bg-slate-950 text-white pt-24 pb-12 border-t border-slate-800 z-10 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center md:items-start gap-12 mb-16">
          <div className="text-center md:text-left">
            <img src={logo} alt="SaleSense Logo" className="h-8 mb-6 mx-auto md:mx-0 brightness-0 invert" />
            <h3 className="text-xl font-bold mb-2">Sailsense AI</h3>
            <p className="text-slate-400">A Product of Provaantech</p>
          </div>
          <div className="flex flex-wrap justify-center md:justify-end gap-x-8 gap-y-4 text-slate-400 font-medium max-w-2xl">
            {['Quick Links', 'Pricing', 'Support', 'Contact', 'Legal'].map((item, i) => (
              <span key={i} className="hover:text-white cursor-pointer transition-colors">{item}</span>
            ))}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 border-t border-slate-800 text-center text-slate-500 font-medium">
          © 2026 Sailsense AI. All rights reserved.
        </div>
      </footer>

    </div>
  );
};

export default Home;